import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { BulleEnrichie, Etat } from '../partage/api/types';
import type { Issue } from './decompteDeLInventaire';
import { Deplacer } from './Deplacer';
import { PastilleEtat } from '../apparence';
import { SupprimerBulle } from '../projets';
import { IDEE } from '../partage/vocabulaire';

/**
 * Une ligne de l'inventaire, qui se lit comme une ligne de liste à cocher : le cercle devant
 * le titre coche, un second clic décoche. « Déplacer », « Garder comme idée » et
 * « Supprimer » restent à droite, pour ce qui va ailleurs, ce qu'on ne fera pas maintenant et
 * ce qui ne concerne pas (ticket 18 de retours-testeurs).
 *
 * Une ligne cochée ne se déplace, ne se garde ni ne se supprime (on la décoche d'abord) ; une
 * ligne gardée comme idée, supprimée ou déplacée ne se coche plus : la reprendre demanderait
 * une mutation inverse que l'inventaire ne reçoit pas.
 */
export function LigneInventaire({
  bulle,
  issue,
  dit,
  dossierId,
  onBasculer,
  onIdee,
  onSupprimee,
  onDeplacer,
}: {
  bulle: BulleEnrichie;
  issue: Issue | undefined;
  /** Un déplacement qui n'a pas eu lieu, dit sous la ligne revenue (chantier `deplacer`, 03). */
  dit?: string;
  /** Le dossier de l'inventaire : déposée dedans, elle ne quitte pas le décompte. */
  dossierId: string;
  onBasculer: () => void;
  onIdee: () => void;
  /** Appelé une fois la suppression confirmée et écrite. */
  onSupprimee: () => void;
  onDeplacer: (parentId: string | null, ou: 'dedans' | 'sortie') => void;
}) {
  const [enDeplacement, setEnDeplacement] = useState(false);
  const [aSupprimer, setASupprimer] = useState(false);

  const cochee = issue?.sorte === 'cochee';
  const idee = issue?.sorte === 'idee';
  const supprimee = issue?.sorte === 'supprimee';
  const deplacee = issue?.sorte === 'deplacee';
  // Une écriture est en route : on ne décoche pas une coche dont on ignore l'état d'avant,
  // et on ne recoche pas avant que le décochage soit écrit.
  const enCours =
    (issue?.sorte === 'cochee' && issue.etatAvant === null) || issue?.sorte === 'decochage';
  const partie = idee || supprimee || deplacee;
  // Une issue est prise : les deux autres s'effacent (le bouton retenu garde sa tuile).
  const prise = cochee || partie || enCours;

  const etatMontre: Etat = cochee ? 'fait' : idee ? 'sans_objet' : bulle.etat;

  return (
    <li className="inventaire-restee" data-bulle={bulle.id}>
      <div className="inventaire-bulle">
        <button
          type="button"
          className="inventaire-cocher"
          aria-pressed={cochee}
          aria-label={cochee ? `décocher « ${bulle.titre} »` : `cocher « ${bulle.titre} »`}
          disabled={partie || enCours}
          onClick={onBasculer}
        >
          <PastilleEtat etat={etatMontre} taille={19} />
        </button>

        <div className="inventaire-dire">
          <div className="inventaire-ligne">
            <p
              className={`inventaire-titre${supprimee ? ' est-supprimee' : ''}${cochee ? ' est-cochee' : ''}`}
            >
              {bulle.titre}
            </p>
            {idee ? (
              <span className="etiquette-idee">{IDEE}</span>
            ) : supprimee ? (
              <span className="inventaire-badge">Supprimée</span>
            ) : (
              <PointsDedans combien={bulle.nbEnfants} />
            )}
          </div>
          {/* La seule chose qui se dit ligne par ligne : ce que le monde n'a pas encore rendu. */}
          {!cochee && bulle.etat === 'en_attente_confirmation' && (
            <p className="inventaire-lecture">Elle attend encore une réponse du monde.</p>
          )}
          {dit && (
            <p className="inventaire-lecture" role="status">
              {dit}
            </p>
          )}
        </div>

        <div className="inventaire-issues">
          <BoutonIssue
            nom="Déplacer"
            geste="deplacer"
            retenue={deplacee}
            horsJeu={prise && !deplacee}
            onChoisir={() => setEnDeplacement(true)}
          />
          <BoutonIssue
            nom="Garder comme idée"
            retenue={idee}
            horsJeu={prise && !idee}
            onChoisir={onIdee}
          />
          <BoutonIssue
            nom="Supprimer"
            retenue={supprimee}
            horsJeu={prise}
            onChoisir={() => setASupprimer(true)}
          />
        </div>
      </div>

      {/* La confirmation du ticket 10, par-dessus l'inventaire : il est déplacé par un
          `transform`, qui garderait une fenêtre fixe dans son cadre. */}
      {aSupprimer &&
        createPortal(
          <SupprimerBulle
            bulle={bulle}
            auDessus
            onGarder={() => setASupprimer(false)}
            onSupprimee={() => {
              setASupprimer(false);
              onSupprimee();
            }}
          />,
          document.body
        )}

      {enDeplacement && (
        <Deplacer
          bulleId={bulle.id}
          titre={bulle.titre}
          combienDedans={bulle.nbEnfants}
          onRenoncer={() => setEnDeplacement(false)}
          onDeposer={(parentId, dossierArrivee) => {
            setEnDeplacement(false);
            onDeplacer(parentId, dossierArrivee === dossierId ? 'dedans' : 'sortie');
          }}
        />
      )}
    </li>
  );
}

/**
 * Un point rond par chose contenue, jamais un nombre écrit : on voit d'un coup d'œil qu'une
 * étape en a d'autres dedans. Au-delà d'une douzaine la planche n'avait pas à trancher : on
 * replie sur un reste chiffré.
 */
function PointsDedans({ combien }: { combien: number }) {
  if (combien === 0) return null;
  const montres = Math.min(combien, 12);

  return (
    <div className="inventaire-dedans" aria-label={`${combien} dedans`}>
      {Array.from({ length: montres }, (_, i) => (
        <span key={i} className="inventaire-grain" />
      ))}
      {combien > montres && <em>+{combien - montres}</em>}
    </div>
  );
}

/**
 * « Déplacer », « Garder comme idée » et « Supprimer » ne sont que du texte tant qu'on n'a
 * rien choisi : ne rien faire vaut garder, trois boutons à égalité feraient croire qu'il faut
 * en prendre un. Celui qu'on a pris porte une tuile ; les autres s'effacent, comme tous quand
 * la ligne est cochée.
 */
function BoutonIssue({
  nom,
  geste,
  retenue,
  horsJeu,
  onChoisir,
}: {
  nom: string;
  /** Pour rendre la main au bouton après le panneau de la boucle. */
  geste?: string;
  retenue: boolean;
  horsJeu: boolean;
  onChoisir: () => void;
}) {
  const classe = retenue ? 'est-retenue' : horsJeu ? 'est-hors-jeu' : '';
  return (
    <button
      type="button"
      data-geste={geste}
      className={`inventaire-issue ${classe}`}
      disabled={horsJeu}
      onClick={onChoisir}
    >
      {nom}
    </button>
  );
}
